import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip
} from '@mui/material';
import {
  Close,
  Assignment,
  BugReport,
  Bookmark,
  CheckCircle,
  AccountTree,
  Comment as CommentIcon,
  Schedule,
  Flag,
  OpenInNew
} from '@mui/icons-material';
import { JiraIssue, JiraComment } from '../../types';
import { getIssueDetails, getIssueComments, JiraApiError } from '../../services/jiraService';

/**
 * Safely extracts human-readable text from Atlassian Document Format (ADF) objects or strings.
 * Prevents "Objects are not valid as a React child" errors when rendering Jira Cloud v3 content.
 */
export const parseJiraContent = (content: any): string => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);

  if (typeof content === 'object') {
    // If it has direct text property (ADF text node)
    if (typeof content.text === 'string') {
      return content.text;
    }

    // If it has content array (e.g. doc, paragraph, bulletList, listItem)
    if (Array.isArray(content.content)) {
      return content.content
        .map((child: any) => parseJiraContent(child))
        .filter(Boolean)
        .join(content.type === 'paragraph' || content.type === 'bulletList' ? '\n' : ' ');
    }

    if (Array.isArray(content)) {
      return content.map(parseJiraContent).filter(Boolean).join('\n');
    }

    if (content.value) return parseJiraContent(content.value);
    if (content.body) return parseJiraContent(content.body);

    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return '';
    }
  }

  return '';
};

interface JiraIssueDetailsDialogProps {
  open: boolean;
  issueKey: string | null;
  onClose: () => void;
  onSelectIssue?: (key: string) => void;
}

export const JiraIssueDetailsDialog: React.FC<JiraIssueDetailsDialogProps> = ({
  open,
  issueKey,
  onClose,
  onSelectIssue
}) => {
  const [issue, setIssue] = useState<JiraIssue | null>(null);
  const [loadingIssue, setLoadingIssue] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);

  const [comments, setComments] = useState<JiraComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !issueKey) {
      setIssue(null);
      setComments([]);
      setIssueError(null);
      setCommentsError(null);
      return;
    }

    let isMounted = true;
    setLoadingIssue(true);
    setLoadingComments(true);
    setIssueError(null);
    setCommentsError(null);

    // Call GET /jira/issues/{issue_key}
    getIssueDetails(issueKey)
      .then((data) => {
        if (isMounted) setIssue(data);
      })
      .catch((err) => {
        if (isMounted) {
          setIssueError(
            err instanceof JiraApiError
              ? err.message
              : `Failed to load details for issue ${issueKey}.`
          );
        }
      })
      .finally(() => {
        if (isMounted) setLoadingIssue(false);
      });

    // Call GET /jira/issues/{issue_key}/comments independently
    getIssueComments(issueKey)
      .then((data) => {
        if (isMounted) setComments(data);
      })
      .catch((err) => {
        if (isMounted) {
          setCommentsError(
            err instanceof JiraApiError
              ? err.message
              : 'Could not load comments for this issue.'
          );
        }
      })
      .finally(() => {
        if (isMounted) setLoadingComments(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, issueKey]);

  const getTypeIcon = (type?: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('bug')) return <BugReport sx={{ fontSize: 16, color: '#FF6577' }} />;
    if (t.includes('story')) return <Bookmark sx={{ fontSize: 16, color: '#1ca87f' }} />;
    if (t.includes('task')) return <CheckCircle sx={{ fontSize: 16, color: 'var(--hp-primary)' }} />;
    return <Assignment sx={{ fontSize: 16, color: 'var(--hp-primary)' }} />;
  };

  const getPriorityColor = (priority?: string) => {
    const p = (priority || '').toLowerCase();
    if (p.includes('highest') || p.includes('high')) return '#FF6577';
    if (p.includes('medium')) return '#FFAA00';
    if (p.includes('low') || p.includes('lowest')) return '#1ca87f';
    return 'var(--hp-text-secondary)';
  };

  const getStatusColor = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('done') || s.includes('closed') || s.includes('resolved')) {
      return { bg: 'rgba(34, 201, 154, 0.14)', text: '#1ca87f' };
    }
    if (s.includes('progress') || s.includes('review')) {
      return { bg: 'rgba(79, 107, 255, 0.14)', text: 'var(--hp-primary)' };
    }
    return { bg: 'var(--hp-border)', text: 'var(--hp-text)' };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '20px',
            bgcolor: 'var(--hp-surface)',
            color: 'var(--hp-text)',
            p: 1
          }
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          {issue?.issueType && (
            <Chip
              icon={getTypeIcon(issue.issueType)}
              label={issue.issueType}
              size="small"
              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
            />
          )}
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-primary)' }}>
            {issueKey}
          </Typography>
          {issue?.status && (
            <Chip
              label={issue.status}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: getStatusColor(issue.status).bg,
                color: getStatusColor(issue.status).text
              }}
            />
          )}
          {issue?.priority && (
            <Chip
              icon={<Flag sx={{ fontSize: 14, color: `${getPriorityColor(issue.priority)} !important` }} />}
              label={issue.priority}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                border: `1px solid ${getPriorityColor(issue.priority)}40`
              }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close dialog">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2 }}>
        {loadingIssue ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1.5 }}>
            <CircularProgress size={36} sx={{ color: 'var(--hp-primary)' }} />
            <Typography variant="body2" color="var(--hp-text-secondary)">
              Fetching Jira issue details from Cloud Run API...
            </Typography>
          </Box>
        ) : issueError ? (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {issueError}
          </Alert>
        ) : issue ? (
          <>
            {/* Issue Summary */}
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--hp-text)' }}>
              {issue.summary}
            </Typography>

            {/* Parent issue link if present */}
            {issue.parent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'var(--hp-bg)', p: 1, borderRadius: '8px' }}>
                <AccountTree sx={{ fontSize: 16, color: 'var(--hp-primary)' }} />
                <Typography variant="caption" color="var(--hp-text-secondary)">Parent:</Typography>
                <Button
                  size="small"
                  onClick={() => onSelectIssue && onSelectIssue(issue.parent!.key)}
                  sx={{ textTransform: 'none', fontWeight: 700, p: 0, minWidth: 'auto', fontSize: '0.8rem' }}
                >
                  {issue.parent.key}
                </Button>
                {issue.parent.summary && (
                  <Typography variant="caption" color="var(--hp-text)">
                    • {issue.parent.summary}
                  </Typography>
                )}
              </Box>
            )}

            {/* Metadata Grid (Assignee, Reporter, Sprint, Dates) */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                p: 1.5,
                bgcolor: 'var(--hp-bg)',
                borderRadius: '12px',
                border: '1px solid var(--hp-border)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Assignee:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'var(--hp-primary)' }}>
                    {(issue.assignee?.displayName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                    {issue.assignee?.displayName || 'Unassigned'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Reporter:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'var(--hp-surface-raised)', color: 'var(--hp-text)' }}>
                    {(issue.reporter?.displayName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                    {issue.reporter?.displayName || 'Unknown'}
                  </Typography>
                </Box>
              </Box>

              {issue.sprint && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Sprint:</Typography>
                  <Chip
                    size="small"
                    icon={<Schedule sx={{ fontSize: 13 }} />}
                    label={issue.sprint.name}
                    sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                  />
                </Box>
              )}

              {issue.projectKey && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Project:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                    {issue.projectName ? `${issue.projectName} (${issue.projectKey})` : issue.projectKey}
                  </Typography>
                </Box>
              )}

              {issue.created && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Created:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                    {new Date(issue.created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              )}

              {issue.updated && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', width: 75 }}>Updated:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                    {new Date(issue.updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Description Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'var(--hp-text)' }}>
                Description
              </Typography>
              <Box
                sx={{
                  p: 1.8,
                  bgcolor: 'var(--hp-bg)',
                  borderRadius: '12px',
                  border: '1px solid var(--hp-border)',
                  fontSize: '0.86rem',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 250,
                  overflowY: 'auto'
                }}
              >
                {parseJiraContent(issue.description) || 'No description provided for this Jira issue.'}
              </Box>
            </Box>

            {/* Linked Issues Section */}
            {issue.linkedIssues && issue.linkedIssues.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'var(--hp-text)' }}>
                  Linked Issues ({issue.linkedIssues.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {issue.linkedIssues.map((linked, idx) => (
                    <Box
                      key={idx}
                      onClick={() => onSelectIssue && onSelectIssue(linked.key)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.2,
                        borderRadius: '10px',
                        bgcolor: 'var(--hp-bg)',
                        border: '1px solid var(--hp-border)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: 'var(--hp-primary-light)', borderColor: 'var(--hp-primary)' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', fontWeight: 600 }}>
                          {linked.relationship || 'relates to'}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--hp-primary)' }}>
                          {linked.key}
                        </Typography>
                        {linked.summary && (
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'var(--hp-text)' }}>
                            • {linked.summary}
                          </Typography>
                        )}
                      </Box>
                      {linked.status && (
                        <Chip
                          label={linked.status}
                          size="small"
                          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Subtasks Section */}
            {issue.subtasks && issue.subtasks.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                    Subtasks ({issue.subtasks.length})
                  </Typography>
                  <Typography variant="caption" color="var(--hp-text-secondary)">
                    {issue.subtasks.filter((s) => (s.status || '').toLowerCase().includes('done')).length}/{issue.subtasks.length} Completed
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {issue.subtasks.map((subtask) => (
                    <Box
                      key={subtask.id || subtask.key}
                      onClick={() => onSelectIssue && onSelectIssue(subtask.key)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.2,
                        borderRadius: '10px',
                        bgcolor: 'var(--hp-bg)',
                        border: '1px solid var(--hp-border)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: 'var(--hp-primary-light)', borderColor: 'var(--hp-primary)' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <CheckCircle sx={{ fontSize: 15, color: 'var(--hp-primary)' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-primary)' }}>
                          {subtask.key}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          • {subtask.summary}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {subtask.status && (
                          <Chip
                            label={subtask.status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              bgcolor: getStatusColor(subtask.status).bg,
                              color: getStatusColor(subtask.status).text
                            }}
                          />
                        )}
                        {subtask.assignee?.displayName && (
                          <Tooltip title={`Assignee: ${subtask.assignee.displayName}`}>
                            <Avatar sx={{ width: 18, height: 18, fontSize: '0.58rem', bgcolor: 'var(--hp-primary)' }}>
                              {subtask.assignee.displayName.charAt(0).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Labels and Components */}
            {((issue.labels && issue.labels.length > 0) || (issue.components && issue.components.length > 0)) && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {issue.labels && issue.labels.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ fontWeight: 600 }}>
                      Labels:
                    </Typography>
                    {issue.labels.map((lbl) => (
                      <Chip key={lbl} label={lbl} size="small" sx={{ height: 18, fontSize: '0.64rem', bgcolor: 'var(--hp-bg)' }} />
                    ))}
                  </Box>
                )}

                {issue.components && issue.components.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ fontWeight: 600 }}>
                      Components:
                    </Typography>
                    {issue.components.map((comp) => (
                      <Chip key={comp} label={comp} size="small" sx={{ height: 18, fontSize: '0.64rem', bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)' }} />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Divider />

            {/* Comments Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CommentIcon sx={{ fontSize: 18, color: 'var(--hp-primary)' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                  Comments {loadingComments ? '...' : `(${comments.length})`}
                </Typography>
              </Box>

              {loadingComments ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'var(--hp-primary)' }} />
                  <Typography variant="caption" color="var(--hp-text-secondary)">
                    Loading issue comments...
                  </Typography>
                </Box>
              ) : commentsError ? (
                <Alert severity="info" sx={{ fontSize: '0.8rem', borderRadius: '8px' }}>
                  {commentsError}
                </Alert>
              ) : comments.length === 0 ? (
                <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ fontSize: '0.84rem', fontStyle: 'italic' }}>
                  No comments recorded on this Jira issue yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {comments.map((comment) => (
                    <Box
                      key={comment.id}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: 'var(--hp-bg)',
                        border: '1px solid var(--hp-border)'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'var(--hp-primary)' }}>
                            {(comment.author?.displayName || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                            {comment.author?.displayName || 'User'}
                          </Typography>
                        </Box>
                        {comment.created && (
                          <Typography variant="caption" color="var(--hp-text-secondary)">
                            {new Date(comment.created).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.84rem', color: 'var(--hp-text)', whiteSpace: 'pre-wrap' }}>
                        {parseJiraContent(comment.body)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {issue ? (
          <Button
            component="a"
            href={`https://scrumaiapp.atlassian.net/browse/${issue.key}`}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.84rem',
              fontWeight: 600,
              color: 'var(--hp-primary)'
            }}
          >
            Open in Jira
          </Button>
        ) : <Box />}
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
